import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DamageAssessment, DamageSeverity, DamageStructureType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, FileHashField, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, type FileHashValue } from '../../ui/components';

const ASSESSOR_ROLES = ['FIELD_ASSESSOR', 'DUTY_OFFICER', 'EOC_COMMANDER'];
const REVIEWER_ROLES = ['DUTY_OFFICER', 'EOC_COMMANDER', 'RELIEF_ADMIN'];
const STRUCTURE_TYPES: DamageStructureType[] = ['RESIDENTIAL', 'COMMERCIAL', 'INFRASTRUCTURE', 'AGRICULTURAL', 'PUBLIC_BUILDING', 'OTHER'];
const SEVERITIES: DamageSeverity[] = ['NONE', 'MINOR', 'MODERATE', 'MAJOR', 'DESTROYED'];
const DISTRICTS = [
  'Beau Vallon', 'Anse Royale', 'Baie Lazare', 'Anse Boileau', 'Takamaka', 'Port Glaud', 'Cascade', 'Au Cap',
  'Anse Aux Pins', 'English River', 'Mont Buxton', 'Plaisance', 'St Louis', 'Roche Caiman', 'Bel Air', 'Mont Fleuri',
  'Glacis', 'Bel Ombre', 'Anse Etoile', 'Machabee', 'Pointe Larue', 'Grand Anse Mahe', 'Grand Anse Praslin', 'Baie Ste Anne',
];

/** Offline-first field damage assessment — DocVault-issued reports when there's real damage attributed to an owner NIN (M36). */
export function DamageAssessments() {
  const { persona } = useAuth();
  const canAssess = persona ? persona.roles.some((r) => ASSESSOR_ROLES.includes(r)) : false;
  const canReview = persona ? persona.roles.some((r) => REVIEWER_ROLES.includes(r)) : false;
  const [severity, setSeverity] = useState('');
  const [district, setDistrict] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (severity) params.set('status', severity);
    if (district) params.set('district', district);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m36/damage-assessments?${params.toString()}`;
  }, [severity, district, appliedQ]);
  const listQ = useApiQuery<Page<DamageAssessment>>(canReview ? path : null);

  return (
    <div>
      <PageHeader
        title="Damage Assessments"
        subtitle="Field-recorded structural damage, offline-first (clientRef dedup on sync) — auto-issues a DocVault report when there's real damage + an owner NIN (M36)."
        actions={
          canAssess ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Record assessment
            </button>
          ) : undefined
        }
      />

      {!canAssess && !canReview ? <RoleNotice roles={[...ASSESSOR_ROLES, ...REVIEWER_ROLES]} /> : null}

      {canReview ? (
        <>
          <form
            className="mb-4 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-56" placeholder="Ref or address…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={district} onChange={(e) => setDistrict(e.target.value)}>
              <option value="">All districts</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select className="field-input !w-auto" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">All severities</option>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>

          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No assessments match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Structure</th>
                    <th className="table-head">Address</th>
                    <th className="table-head">District</th>
                    <th className="table-head">Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((a) => (
                    <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{a.refNo}</td>
                      <td className="table-cell">
                        <StatusBadge status={a.structureType} />
                      </td>
                      <td className="table-cell text-xs text-slate-500">{a.address}</td>
                      <td className="table-cell text-xs text-slate-500">{a.district}</td>
                      <td className="table-cell">
                        <StatusBadge status={a.severity} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? <DetailModal id={selectedId} onClose={() => setSelectedId(null)} /> : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useApiQuery<DamageAssessment>(`/api/v1/m36/damage-assessments/${id}`);
  const a = q.data;
  return (
    <Modal title={a ? a.refNo : 'Damage assessment'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {a ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Structure" value={<StatusBadge status={a.structureType} />} />
            <Detail label="Severity" value={<StatusBadge status={a.severity} />} />
            <Detail label="Address" value={a.address} />
            <Detail label="District" value={a.district} />
            <Detail label="Owner" value={a.ownerName ?? a.ownerNin ?? '—'} />
            <Detail label="Assessed by" value={a.assessedBy} mono />
          </div>
          {a.notes ? <p className="text-xs text-slate-500">{a.notes}</p> : null}
          {a.photos.length > 0 ? <p className="text-xs text-slate-500">{a.photos.length} photo(s) on file.</p> : null}
          {a.reportDocId ? <p className="text-xs text-seagreen-600">DocVault damage report issued: {a.reportDocId}</p> : <p className="text-xs text-slate-400">No DocVault report — requires severity ≠ NONE and an owner NIN.</p>}
          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Assessed {formatDateTime(a.assessedAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [structureType, setStructureType] = useState<DamageStructureType>('RESIDENTIAL');
  const [severity, setSeverity] = useState<DamageSeverity>('MODERATE');
  const [ownerNin, setOwnerNin] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [address, setAddress] = useState('');
  const [district, setDistrict] = useState(DISTRICTS[0]!);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<FileHashValue | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const useDeviceLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setLocating(false);
      },
      () => setLocating(false),
    );
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const clientRef = `dmg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await api.post('/api/v1/m36/field/damage-assessments', {
        clientRef,
        structureType,
        severity,
        ownerNin: ownerNin || undefined,
        ownerName: ownerName || undefined,
        address,
        district,
        lat: lat ? Number.parseFloat(lat) : undefined,
        lng: lng ? Number.parseFloat(lng) : undefined,
        notes: notes || undefined,
        photos: photo ? [{ docRef: photo.docRef, sha256: photo.sha256 }] : [],
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record assessment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record field damage assessment" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">A clientRef is minted client-side so a retried sync of this same assessment is a genuine no-op — the offline-first convention this endpoint is built for.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Structure type">
            <select className="field-input" value={structureType} onChange={(e) => setStructureType(e.target.value as DamageStructureType)}>
              {STRUCTURE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Severity">
            <select className="field-input" value={severity} onChange={(e) => setSeverity(e.target.value as DamageSeverity)}>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owner NIN (for relief eligibility + DocVault report)">
            <input className="field-input" value={ownerNin} onChange={(e) => setOwnerNin(e.target.value)} />
          </Field>
          <Field label="Owner name (optional)">
            <input className="field-input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Address">
            <input className="field-input" value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>
          <Field label="District">
            <select className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)}>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="GPS latitude">
            <input className="field-input" type="number" step="0.000001" value={lat} onChange={(e) => setLat(e.target.value)} />
          </Field>
          <Field label="GPS longitude">
            <input className="field-input" type="number" step="0.000001" value={lng} onChange={(e) => setLng(e.target.value)} />
          </Field>
        </div>
        <button className="btn-secondary" type="button" disabled={locating} onClick={useDeviceLocation}>
          {locating ? 'Reading location…' : "Use this device's location"}
        </button>
        <FileHashField label="Damage photo (optional)" onChange={setPhoto} />
        <Field label="Notes (optional)">
          <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !address || !district} onClick={submit}>
            {busy ? 'Saving…' : 'Record assessment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
